import BackArrow from '@assets/icons/back-arrow.svg';
import i18n from '@/utils/i18n';
import React, { useMemo, useRef } from 'react';
import { ActivityIndicator } from '@/components/activity-indicator';
import {
  BORDER_RADIUS_SMALL,
  FONTS,
  SCREEN_PADDING,
  SCREEN_PADDING_LARGE,
  SCREEN_PADDING_SMALL,
  TYPOGRAPHY,
} from '@/styles';
import { BRAND_PAGE_SOURCE, BrandStorefrontModule } from '@/utils/brand-page';
import { BrandPageCollection } from './brand-page-collection';
import { BrandPageFieldsFragment } from '@/types';
import { BrandPageHeader } from './brand-page-header';
import { BrandPageImageHeader } from './brand-page-image-header';
import { createStyles } from '@/contexts/theme-context';
import { ExpandableText } from '@/components/expandable-text';
import { HeaderBackButton } from '@/components/header-back-button';
import { ItemListOption } from '@/contexts/item-context-types';
import { Props as ListItemProps } from '@/components/list-item';
import { MaxWidthLargeContent } from '@/components/max-width-large-content';
import { OrderableItemList } from '@/components/orders/orderable-item-list';
import { OrderableItemListItemWithRowQuantity } from '@/components/orders/orderable-item-list-item';
import { ResizeMode, Video } from 'expo-av';
import { Text, View } from 'react-native';
import { useItemDetailContext } from '@/contexts/item-detail-context';
import { useLayoutContext } from '@/contexts/layout-context';
import { useNavigation } from '@react-navigation/native';
import { useNewOrderActionsContext } from '@/contexts/new-order-actions-context';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

const VIDEO_ASPECT_RATIO = 16 / 9;

interface Props {
  brandPageContent?: BrandPageFieldsFragment | null;
  collections: BrandPageCollection[];
  loading: boolean;
  variantPacks: ItemListOption[];
}

export const BrandPageComponent: React.FC<Props> = ({
  brandPageContent,
  collections,
  loading,
  variantPacks,
}) => {
  const storefrontModule: BrandStorefrontModule | null =
    brandPageContent?.storefront_module;
  const { bottom } = useSafeAreaInsets();
  const { goBack } = useNavigation();
  const [styles] = useStyles();
  const { debouncedUpdateQuantity } = useNewOrderActionsContext();
  const { selectItemListOption } = useItemDetailContext();
  const { isSmallLayout, isSmallTabletLayout } = useLayoutContext();
  const videoRef = useRef<Video>(null);

  const listItemProps: Partial<ListItemProps<ItemListOption>> = useMemo(() => {
    return {
      onSelect: (item) =>
        selectItemListOption(item, { source: BRAND_PAGE_SOURCE }),
      renderMainContent: (option) => {
        return (
          <OrderableItemListItemWithRowQuantity
            isFocused={false}
            onOptionsPress={(item) =>
              selectItemListOption(item, { source: BRAND_PAGE_SOURCE })
            }
            option={option}
            source={BRAND_PAGE_SOURCE}
            updateQuantity={(updateQuantityData) => {
              debouncedUpdateQuantity(updateQuantityData, {
                source: BRAND_PAGE_SOURCE,
              });
            }}
          />
        );
      },
    };
  }, [debouncedUpdateQuantity, selectItemListOption]);

  const brandPageContentText = storefrontModule ? (
    isSmallLayout ? (
      <ExpandableText
        numberOfLines={4}
        textStyle={TYPOGRAPHY.small}
        trackingData={{
          name: 'brand_page.description',
          properties: {
            brandPageUUID: brandPageContent?.uuid,
          },
        }}
      >
        {storefrontModule.content_text}
      </ExpandableText>
    ) : (
      <Text
        style={[
          styles.contentText,
          isSmallTabletLayout
            ? styles.contentTextTablet
            : styles.contentTextDesktop,
        ]}
      >
        {storefrontModule.content_text}
      </Text>
    )
  ) : null;

  const brandPageVideo = storefrontModule?.video_url ? (
    <Video
      onLoad={() =>
        videoRef.current?.setStatusAsync({
          isLooping: false,
          isMuted: true,
          shouldPlay: true,
        })
      }
      ref={videoRef}
      resizeMode={ResizeMode.CONTAIN}
      source={{ uri: storefrontModule.video_url }}
      style={isSmallLayout ? styles.video : styles.videoLarge}
      testID="brand-page-video"
      useNativeControls
      // Required for web
      videoStyle={isSmallLayout ? styles.video : styles.videoLarge}
    />
  ) : null;

  const brandPageContents = brandPageContent ? (
    <>
      {storefrontModule ? (
        isSmallLayout ? (
          <>
            <View style={[styles.section, styles.topSection]}>
              <Text style={TYPOGRAPHY.h2}>{storefrontModule.headline}</Text>
              {brandPageContentText}
            </View>
            {storefrontModule.video_url ? (
              <View style={styles.videoContainer}>{brandPageVideo}</View>
            ) : null}
          </>
        ) : (
          <View style={styles.contentContainer}>
            <View style={styles.flex}>
              <BrandPageImageHeader brandPageContent={brandPageContent} />
            </View>
            <View style={[styles.flex, styles.contentContainerRight]}>
              <View style={styles.headline}>
                <Text
                  style={
                    isSmallTabletLayout
                      ? TYPOGRAPHY.brandHeadlineSmall
                      : TYPOGRAPHY.brandHeadline
                  }
                >
                  {storefrontModule.headline}
                </Text>
                {brandPageContentText}
              </View>
            </View>
          </View>
        )
      ) : null}
      {collections.map((collection) => (
        <BrandPageCollection
          collection={collection}
          headerStyle={styles.collection}
          key={collection.title}
        />
      ))}
      {!isSmallLayout && storefrontModule?.video_url ? (
        <View style={styles.flex}>
          <View style={[styles.videoContainerLarge, styles.flex]}>
            {brandPageVideo}
          </View>
        </View>
      ) : null}
      <Text style={[TYPOGRAPHY.h3, styles.section]}>
        {i18n.t('brand_page.all_products', {
          brandName: brandPageContent.brand_name,
        })}
      </Text>
    </>
  ) : null;

  return isSmallLayout ? (
    <BrandPageHeader brandPageContent={brandPageContent}>
      {({ ListHeaderComponent, onScroll, scrollViewAnimatedStyle }) =>
        !brandPageContent || loading ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator />
          </View>
        ) : (
          <OrderableItemList
            animated
            listContentContainerStyle={{
              paddingBottom: Math.max(bottom, SCREEN_PADDING),
            }}
            listData={variantPacks}
            ListHeaderComponent={
              <>
                {ListHeaderComponent}
                {brandPageContents}
              </>
            }
            listItemProps={listItemProps}
            scrollViewProps={{ onScroll, style: scrollViewAnimatedStyle }}
          />
        )
      }
    </BrandPageHeader>
  ) : (
    <MaxWidthLargeContent>
      <View style={styles.headerLarge}>
        <HeaderBackButton
          containerStyle={styles.backButtonLarge}
          Icon={BackArrow}
          onPress={goBack}
        />
        <Text style={[TYPOGRAPHY.h3, styles.headerTitleLarge]}>
          {brandPageContent?.brand_name}
        </Text>
      </View>
      {loading || !brandPageContent ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator />
        </View>
      ) : (
        <OrderableItemList
          listData={variantPacks}
          ListHeaderComponent={brandPageContents}
          listItemProps={listItemProps}
          showListView
        />
      )}
    </MaxWidthLargeContent>
  );
};

const useStyles = createStyles((theme) => ({
  backButtonLarge: {
    marginLeft: 0,
    paddingVertical: 0,
  },
  collection: {
    paddingBottom: 8,
    paddingHorizontal: SCREEN_PADDING_SMALL,
    paddingTop: SCREEN_PADDING_SMALL,
  },
  contentContainer: {
    borderRadius: BORDER_RADIUS_SMALL,
    flexDirection: 'row',
    marginBottom: SCREEN_PADDING_LARGE,
    marginHorizontal: SCREEN_PADDING_SMALL,
    overflow: 'hidden',
  },
  contentContainerRight: {
    backgroundColor: theme.sk.color.bg.secondary.default,
    justifyContent: 'center',
  },
  contentText: {
    ...FONTS.primary,
    color: theme.colors.primary,
    lineHeight: 24,
  },
  contentTextDesktop: {
    fontSize: theme.fontSizes.medium,
  },
  contentTextTablet: {
    fontSize: theme.fontSizes.small,
  },
  flex: {
    flex: 1,
  },
  headerLarge: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'center',
    paddingHorizontal: SCREEN_PADDING_SMALL,
    paddingVertical: SCREEN_PADDING,
  },
  headerTitleLarge: {
    flex: 1,
    paddingRight: SCREEN_PADDING_LARGE,
    textAlign: 'center',
  },
  headline: {
    gap: 32,
    paddingHorizontal: 48,
    paddingVertical: 38,
  },
  loadingContainer: {
    alignItems: 'center',
    flex: 1,
    justifyContent: 'center',
  },
  section: {
    padding: SCREEN_PADDING_SMALL,
  },
  topSection: {
    gap: 8,
  },
  video: {
    aspectRatio: VIDEO_ASPECT_RATIO,
    width: '100%',
  },
  videoContainer: {
    marginVertical: 8,
  },
  videoContainerLarge: {
    marginBottom: 32,
    paddingHorizontal: SCREEN_PADDING_SMALL,
  },
  videoLarge: {
    aspectRatio: VIDEO_ASPECT_RATIO,
    borderRadius: BORDER_RADIUS_SMALL,
    width: '100%',
  },
}));
